import { isNullOrUndefined } from '@app/shared/functions/common.function';
import { catchError, Observable } from 'rxjs';

import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpStatusCode } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';

import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ValidateAccountCommand } from '@app/modules/authentication/commands/validate-account-command';
import { AuthenticationService } from '@app/modules/authentication/services/authentication.service';
import { ModalPoliticsComponent } from '@app/shared/components/modal-politics/modal-politics.component';
import { CustomHttpStatusCode } from '../enums/custom-http-status-code.enum';
import { HttpServerResponse } from '../models/response.model';
import { CredentialsService } from '../services/credentials.service';
import { LoadingService } from '../services/loading.service';
import { LoggerService } from '../services/logger.service';
import { SaveObjectInLocalServiceService } from '../services/save-object-in-local-service.service';

const logger = new LoggerService('HttpErrorInterceptor');

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  protected authenticationService: AuthenticationService;
  protected credentialsService: CredentialsService;
  protected loadingService: LoadingService;
  protected storage: SaveObjectInLocalServiceService;
  dialogRef!: MatDialogRef<ModalPoliticsComponent> | null;

  constructor(private readonly snackBar: MatSnackBar, private readonly router: Router, private readonly injector: Injector, private readonly dialog: MatDialog) {
    this.authenticationService = this.injector.get<AuthenticationService>(AuthenticationService);
    this.credentialsService = this.injector.get<CredentialsService>(CredentialsService);
    this.loadingService = this.injector.get<LoadingService>(LoadingService);
    this.storage = this.injector.get<SaveObjectInLocalServiceService>(SaveObjectInLocalServiceService);
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((response: HttpErrorResponse) => {
        this.loadingService.show();
        if (response.error instanceof ErrorEvent) {
          // CLIENT-SIDE ERROR
          this.snackBar.open('Ocurrio un error al ejectar la petición');
        } else {
          // SERVER-SIDE ERROR
          switch (response.status) {
            case CustomHttpStatusCode.UnknownError: {
              this.snackBar.open('Sin conexión');
              break;
            }
            case HttpStatusCode.BadRequest:
            case HttpStatusCode.InternalServerError:
            case HttpStatusCode.BadGateway: {
              const error: { description: string | undefined } = response.error;
              const errorDesc: string =
                isNullOrUndefined(error.description) || error.description === '' ? 'Ocurrio un error al procesar la acción' : (error.description as string);
              this.snackBar.open(errorDesc);
              break;
            }
            case HttpStatusCode.Unauthorized: {
              this.processToLogin();
              break;
            }
            case HttpStatusCode.Forbidden: {
              this.snackBar.open('No tiene permisos para ejecutar dicha acción', '');
              break;
            }
            case HttpStatusCode.MethodNotAllowed: {
              this.snackBar.open(response.error);
              break;
            }
            case HttpStatusCode.PreconditionFailed: {
              void this.router.navigateByUrl('/authentication/login#retail');
              break;
            }
            case HttpStatusCode.PreconditionRequired: {
              this.processPreconditionRequired(response);
              break;
            }
            case HttpStatusCode.NotAcceptable:
            case HttpStatusCode.NotFound:
            default:
              // INFO: Especify what going to do for errors
              break;
          }
        }

        throw response;
      })
    );
  }

  private processToLogin(): void {
    const snackBarRef = this.snackBar.open('Usuario no autorizado');
    snackBarRef.afterDismissed().subscribe(() => {
      this.credentialsService.setCredentials();
      void this.router.navigate(['/authentication/login']);
    });
  }

  private processPreconditionRequired(response: HttpErrorResponse): void {
    if (this.dialogRef !== undefined && this.dialogRef !== null) {
      throw response;
    }

    this.dialogRef = this.dialog.open(ModalPoliticsComponent, {
      data: {
        title: 'Términos y condiciones de uso',
        message: '',
        acceptLabel: 'Continuar',
        showAccept: true
      }
    });

    this.dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        const valuessession = (this.storage.getObject('session') as string).split('|');
        const validateAccountCommand: ValidateAccountCommand = { userName: valuessession[2] };
        this.authenticationService.getPolicisAccep(validateAccountCommand).subscribe({
          next: (authResponse: HttpServerResponse<unknown>) => {
            logger.debug(authResponse);
            throw response;
          },
          error: (e) => {
            logger.error(e);
            this.dialogRef = null;
            window.location.reload();
            throw response;
          },
          complete: () => {
            this.dialogRef = null;
          }
        });
      } else {
        window.location.reload();
      }
    });
  }
}
